/**
 * listLimits — character-count thresholds for list content.
 *
 * Why these numbers?
 *
 * Firestore caps a single document at 1 MiB (1,048,576 bytes). A list
 * doc bundles content alongside title + position + timestamps, so
 * realistic ceiling for the `content` field is roughly 1,040,000 bytes.
 * UTF-8 ASCII is 1 byte/char; mixed text 1-4. For typical English-ish
 * content that's ~1,000,000 chars at the byte ceiling.
 *
 * We set the HARD limit at 950,000 chars — well below the byte cap so
 * even worst-case multi-byte content can't trip Firestore. The SOFT
 * warning fires at 800,000 chars, giving the user ~150k chars of
 * runway to split or archive.
 *
 * Performance side: typing latency in a controlled <textarea> stays
 * smooth up to ~500KB on mid-range devices; gets noticeable around
 * 800KB-1MB. The soft warning's secondary purpose is nudging users
 * away from that lag zone.
 */

export const LIST_CHAR_SOFT_LIMIT = 800000;
export const LIST_CHAR_HARD_LIMIT = 950000;

/**
 * applyListSizeGuard
 *
 * Call from a controlled-textarea onChange handler before persisting.
 *
 * Returns { content, blocked, justCrossedSoft } where:
 *   - content: the value to commit. When `blocked` is true this is
 *              the PREVIOUS value — we refuse the input outright
 *              rather than truncate, so a paste in the middle of
 *              a long doc can't silently drop content from the end.
 *   - blocked: true if the new value would have exceeded the hard
 *              cap. UI should toast that the paste/keystroke was
 *              refused.
 *   - justCrossedSoft: true on the single edit that pushed length
 *                      past LIST_CHAR_SOFT_LIMIT. UI should toast
 *                      a one-time warning. False if already over
 *                      or still under.
 *
 * The function is pure and stateless — caller compares prev/next
 * lengths so we can fire the soft warning exactly once per crossing.
 *
 * Why refuse rather than truncate? An earlier version sliced
 * `nextContent.slice(0, HARD_LIMIT)`, which kept the first N chars
 * regardless of where the user pasted. If they pasted into the middle
 * of an already-long document, the tail of their old content vanished
 * silently. Refusing the input is uglier but loss-free.
 */
export function applyListSizeGuard(prevContent, nextContent) {
  const prevLen = (prevContent || '').length;
  const nextLen = (nextContent || '').length;

  // Hard cap: refuse the change. The user's textarea snaps back to
  // the previous value via the controlled <textarea value> binding,
  // so keystrokes/pastes that would exceed the limit are no-ops.
  if (nextLen > LIST_CHAR_HARD_LIMIT) {
    return {
      content: prevContent || '',
      blocked: true,
      justCrossedSoft: false,
    };
  }

  // Soft warning: fires only on the first crossing so we don't spam
  // toasts every keystroke once over the line.
  const justCrossedSoft =
    prevLen <= LIST_CHAR_SOFT_LIMIT && nextLen > LIST_CHAR_SOFT_LIMIT;

  return {
    content: nextContent,
    blocked: false,
    justCrossedSoft,
  };
}
